// cSpell:ignore aandeel Aanvulling afgerekend Afrekening afrekening Bedrag bedrag betaald betaalstatus betaaluitnodiging deze eigenaar forfait Huurder huurder Keuze kosten kostenlijst Naam overschot periode privé provisie Provisies regel Samenvatting unticked verbruik Verdeling vertrek voor
// Domain types for cost settlements.
// Mirrors spec §2 of the settlements spec.

import { ENoVatType } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import type { IProvisionLine } from "./contractProvision.utils";
import { isCostPeriodTooLong } from "./costPeriod.utils";
import type { IDepartureCorrection } from "./departureCorrection.utils";
import type { OwnerResult } from "./engine";
import {
  fromCents,
  roundCents,
  tenantShareCents,
  toCents,
} from "./money.utils";
import type { IPayerCandidate } from "./payer.utils";
import type {
  IIndexedProvisionField,
  ProvisionSchedule,
  ProvisionSlice,
} from "./provisionHistory";
import type { StatementSnapshot } from "./scenes/SettlementInfo/SettlementForm/steps/buildStatementSnapshot";
import type {
  ISettlementKind,
  TFixedFeeKindKey,
} from "./settlementKinds.utils";
import { settlementDisplayName } from "./settlementName.utils";

// VAT value: a percentage, or a "no VAT" reason (= effectively 0%). Mirrors the
// contract/payment VAT field (VAT_OPTIONS_BOOKKEEPING) so costs use the same set.
export type VatValue = number | ENoVatType;

export const VAT_OPTIONS: VatValue[] = [
  21,
  12,
  6,
  ENoVatType.Domestic0PercentOnDeclaration,
  ENoVatType.NotOnDeclaration,
  ENoVatType.CoContractorSales,
];

// Reuse Rentio's official no-VAT labels instead of minting duplicates: the
// contract and payment screens show these exact strings, so a reason reads
// the same everywhere. Their wording belongs to those screens; correct it in
// Lokalise, not here.
const NO_VAT_KEYS: Record<ENoVatType, string> = {
  [ENoVatType.Domestic0PercentOnDeclaration]:
    "contract.item.no_vat_type.zero_percent",
  [ENoVatType.NotOnDeclaration]: "contract.item.no_vat_type.not_on_declaration",
  [ENoVatType.CoContractorSales]:
    "contract.item.no_vat_type.co_contractor_sales",
};

export const vatLabel = (v: VatValue): string =>
  typeof v === "number"
    ? getLocalizedText("costs.vat.percentage", { percentage: String(v) })
    : getLocalizedText(NO_VAT_KEYS[v]);

// Effective VAT percentage used in calculations (a "no VAT" reason → 0%).
export const vatPercent = (v: VatValue): number =>
  typeof v === "number" ? v : 0;

export type InvoiceScope = "common" | "private";
export type InvoiceTarget = "settlement" | "direct" | "owner" | "shared";
export type DistributionMethod = "key" | "consumption" | "company";

export const CATEGORIES = [
  "Other",
  "Management",
  "Taxes",
  "Electricity",
  "Fees",
  "Gas",
  "MajorRepairs",
  "LoanInterest",
  "MinorRepairs",
  "Elevator",
  "HeatingOil",
  "Maintenance",
  "PropertyTax",
  "Cleaning",
  "MortgageInsurance",
  "SyndicSettlement",
  "SyndicProvision",
  "Telecom",
  "LoanRepayment",
  "Heating",
  "Insurance",
  "Water",
] as const;

export type Category = (typeof CATEGORIES)[number];

// Category → i18n key. `CATEGORY_LABELS_NL` keeps its name (used across the
// feature) but now resolves through Lokalise instead of hardcoded Dutch.
export const CATEGORY_KEYS: Record<Category, string> = {
  Other: "costs.category.other",
  Management: "costs.category.management",
  Taxes: "costs.category.taxes",
  Electricity: "costs.category.electricity",
  Fees: "costs.category.fees",
  Gas: "costs.category.gas",
  MajorRepairs: "costs.category.major_repairs",
  LoanInterest: "costs.category.loan_interest",
  MinorRepairs: "costs.category.minor_repairs",
  Elevator: "costs.category.elevator",
  HeatingOil: "costs.category.heating_oil",
  Maintenance: "costs.category.maintenance",
  PropertyTax: "costs.category.property_tax",
  Cleaning: "costs.category.cleaning",
  MortgageInsurance: "costs.category.mortgage_insurance",
  SyndicSettlement: "costs.category.syndic_settlement",
  SyndicProvision: "costs.category.syndic_provision",
  Telecom: "costs.category.telecom",
  LoanRepayment: "costs.category.loan_repayment",
  Heating: "costs.category.heating",
  Insurance: "costs.category.insurance",
  Water: "costs.category.water",
};

export const categoryLabel = (c: Category): string =>
  getLocalizedText(CATEGORY_KEYS[c]);

// Proxy so existing `CATEGORY_LABELS_NL[cat]` call sites keep working while
// resolving through i18n at read time (labels must follow the user's locale,
// not the module-load locale).
export const CATEGORY_LABELS_NL: Record<Category, string> = new Proxy(
  {} as Record<Category, string>,
  {
    get: (_t, prop: string) =>
      CATEGORY_KEYS[prop as Category]
        ? getLocalizedText(CATEGORY_KEYS[prop as Category])
        : prop,
    // The target stays empty, so enumeration has to be trapped too — without
    // these, `Object.keys/entries/values` yield nothing and callers that build
    // a list from the record (the category filter) come up empty.
    ownKeys: () => Object.keys(CATEGORY_KEYS),
    getOwnPropertyDescriptor: (_t, prop: string) =>
      CATEGORY_KEYS[prop as Category]
        ? {
            value: getLocalizedText(CATEGORY_KEYS[prop as Category]),
            enumerable: true,
            configurable: true,
            writable: false,
          }
        : undefined,
    has: (_t, prop: string) => prop in CATEGORY_KEYS,
  },
);

export interface CategoryMeta {
  methods: DistributionMethod[];
  defaultTenantSharePct: number;
  ownerOnly: boolean;
}

// Spec §2.2 — defaults; always overridable per invoice.
export const CATEGORY_META: Record<Category, CategoryMeta> = {
  Cleaning: { methods: ["key"], defaultTenantSharePct: 100, ownerOnly: false },
  Maintenance: {
    methods: ["key"],
    defaultTenantSharePct: 50,
    ownerOnly: false,
  },
  Elevator: { methods: ["key"], defaultTenantSharePct: 100, ownerOnly: false },
  Telecom: { methods: ["key"], defaultTenantSharePct: 100, ownerOnly: false },
  Insurance: { methods: ["key"], defaultTenantSharePct: 0, ownerOnly: false },
  Management: { methods: ["key"], defaultTenantSharePct: 0, ownerOnly: false },
  Fees: { methods: ["key"], defaultTenantSharePct: 34, ownerOnly: false },
  SyndicProvision: {
    methods: ["key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  SyndicSettlement: {
    methods: ["key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  Electricity: {
    methods: ["key", "consumption"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  Water: {
    methods: ["consumption", "company", "key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  Heating: {
    methods: ["company", "consumption", "key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  Gas: {
    methods: ["company", "consumption", "key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  HeatingOil: {
    methods: ["company", "consumption", "key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  PropertyTax: { methods: [], defaultTenantSharePct: 0, ownerOnly: true },
  Taxes: { methods: ["key"], defaultTenantSharePct: 0, ownerOnly: false },
  MinorRepairs: {
    methods: ["key"],
    defaultTenantSharePct: 100,
    ownerOnly: false,
  },
  MajorRepairs: { methods: [], defaultTenantSharePct: 0, ownerOnly: true },
  Other: { methods: ["key"], defaultTenantSharePct: 50, ownerOnly: false },
  LoanInterest: { methods: [], defaultTenantSharePct: 0, ownerOnly: true },
  LoanRepayment: { methods: [], defaultTenantSharePct: 0, ownerOnly: true },
  MortgageInsurance: { methods: [], defaultTenantSharePct: 0, ownerOnly: true },
};

// Ownership costs (property tax, major repairs, the loan and its insurance)
// can never be passed on to a residential tenant. The cost popup locks them to
// the owner, the API refuses any other target, and the engine bills an older
// record with another target to the owner all the same. Mirrored in
// rentio-api-next (OWNER_ONLY_COST_CATEGORIES).
export const isOwnerOnlyCategory = (category: Category): boolean =>
  !!CATEGORY_META[category]?.ownerOnly;

// The categories as dropdown options, alphabetical in the user's own language
// (the hard-coded order follows the Dutch labels). The cost popup and the
// cost list filter share it, so both lists always read the same.
export const sortedCategoryOptions = (): {
  value: Category;
  label: string;
}[] => {
  const collatorLocale = getLocale().replace("_", "-");
  return CATEGORIES.map(category => ({
    value: category,
    label: categoryLabel(category),
  })).sort((left, right) =>
    left.label.localeCompare(right.label, collatorLocale),
  );
};

// One payment-order's worth of state on a multi-payer direct cost. Frontend
// persists these locally; the backend takes them over.
export interface PaymentSplit {
  id: string;
  payerId: number;
  payerName: string;
  amount: number;
  // The lease and the unit property the split bills, so a tenant the cost
  // popup did not bill yet can be billed later (absent on older costs).
  contractId?: string;
  propertyId?: number;
  // What the split's payment asks on top of `amount` (see leaseVat.utils;
  // absent on older costs): its part of the supplier's VAT, included in the
  // amount it asks at 0%, or, for a VAT-taxable lease billed at a rate the
  // broker chose, that rate. Stored for a tenant billed later and for the
  // cost detail, so both use the cents of the cost popup.
  supplierVat?: number;
  outgoingVatPct?: number;
  paymentOrderId?: string;
  // The payment-request inside that order (cost-payments are one-off, so one
  // request per order). Stored at create-time so the cost-detail can dispatch
  // markPaid/markUnPaid without needing to refetch the order.
  paymentRequestId?: number;
  requestedAt?: string;
  paidAt?: string;
}

// How the cost popup split a group cost over its units when the cost was
// settled right away (immediate payment or already paid): the detail then
// shows the split that was billed. `weights` holds the weight per unit
// property id the split used (key weight, meter reading or amount).
export interface ICostDistribution {
  method: "key" | "consumption" | "amount";
  keyId?: string;
  keyName?: string;
  consumptionUnit?: "kwh" | "m3";
  weights: Record<string, number>;
}

export interface Invoice {
  id: string;
  groupId?: string;
  unitId?: string;
  scope: InvoiceScope;
  date: string;
  periodFrom: string;
  periodTo: string;
  category: Category;
  categoryOther?: string; // free text when category === "Other"
  supplier: string;
  description?: string;
  amountExclVat: number;
  vatPct: VatValue;
  target: InvoiceTarget;
  tenantSharePct?: number;
  paidAt?: string; // set when the cost is marked as paid
  // Set once the payment order(s) for a "direct" cost were actually created
  // and sent. Until then a direct cost is "still to be invoiced".
  paymentRequestedAt?: string;
  // Per-payer state for multi-payer direct costs. When set, UI derives the
  // overall "betaald" status from these instead of the single fields above.
  paymentSplits?: PaymentSplit[];
  // Group cost settled right away: the split it was billed with. Absent for a
  // cost whose split is left to the settlement, and for older costs.
  distribution?: ICostDistribution;
  documentUrl?: string;
  // Shared UUID linking all unit costs that were created from one group-cost
  // bulk action. NULL for standalone unit costs. When set, siblings can be
  // fetched to reconstruct the group aggregation (total, per-unit split, etc.).
  groupCostRef?: string;
  // Read-only, from the API: an owner who sees only their own part of a group
  // with other owners. `paymentSplits` then holds only their tenants and
  // `distribution` only their units' amounts or readings.
  viewerPart?: boolean;
}

export interface ApportionmentKey {
  id: string;
  groupId: string;
  name: string;
  isSystem: boolean;
  isEqual: boolean;
  weights: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

// Mirrors EContractCostsPriceType: common costs are billed either as a fixed
// amount (nothing to settle) or as a provision (advance, reconciled later).

export interface ContractRef {
  id: string;
  unitId: string;
  unitCode: string;
  // Every tenant on the lease joined ("Hans Huurder & Henry Huurder"); a lease
  // without a tenant member names its parent/guardian.
  tenantName: string;
  // The payer's account: who the payment request and the statement are
  // addressed to, in their language. Whoever pays the lease's rent, else its
  // only tenant or parent; when that is not clear, the first tenant (else a
  // parent) as a guess, with `isPayerResolved` false.
  tenantAccountId?: number;
  // Everyone who can receive the lease's payment request (its tenants and
  // parents, and whoever pays the rent), and whether `tenantAccountId` is
  // certain or the broker has to choose among them.
  payerCandidates?: IPayerCandidate[];
  isPayerResolved?: boolean;
  // The lease's tenants and the parents/guardians who are not tenants, so the
  // settlement shows everyone on the lease.
  tenantNames?: string[];
  parentNames?: string[];
  startDate: string;
  endDate: string;
  monthlyProvision?: number; // full provision: common + non-common + extra lines
  // The two slices of that provision. A group settlement may only consume the
  // common slice, a unit settlement (of a unit inside a group) only the
  // private one — see the contracts memo in SettlementForm. All three are
  // what the tenant pays, so incl. the VAT the lease charges on each line.
  monthlyProvisionCommon?: number;
  monthlyProvisionPrivate?: number;
  // The contract prices that kind of its costs as a flat rate (forfait).
  // A forfait is never settled (Lewis, 2026-10-16, sharpening F35): the
  // engine leaves that kind out for this tenant, no costs, no provision, no
  // balance, and the tenant's share of those costs stays with the owner (see
  // forfaitKindsOf). forfaitPrivate is the non-common field only; an extra
  // line that is a provision never makes it one to settle (decision 20).
  fixedFeeCommon?: boolean;
  fixedFeePrivate?: boolean;
  // Which provision slices this settlement weighs (set per settlement in
  // SettlementForm) and the provision's history, so a provision that changed
  // during the period is not counted at today's amount for every month.
  provisionSlices?: ProvisionSlice[];
  provisionSchedule?: ProvisionSchedule;
  // Per slice: the provision still equals the contract's start amount.
  provisionUnchanged?: Record<ProvisionSlice, boolean>;
  // Per slice: the lease field its indexations track, absent when that field
  // is no provision (see provisionHistory).
  provisionIndexed?: Partial<Record<ProvisionSlice, IIndexedProvisionField>>;
  // Per slice: the lease charges VAT on (part of) that provision.
  provisionVatIncluded?: Record<ProvisionSlice, boolean>;
  // Per slice: the VAT factor of all its provision lines, null when they
  // differ (see contractProvision.utils).
  provisionVatFactor?: Partial<Record<ProvisionSlice, number | null>>;
  // The lease's provision lines as the contract names them (see
  // contractProvision.utils): what the provisions step shows per tenant. The
  // engine settles per slice only.
  provisionLines?: IProvisionLine[];
  // The lease is VAT-taxable (it charges VAT on its rent or a cost line): a
  // cost passed on to it does not simply carry the supplier's VAT, so the
  // broker decides (see leaseVat.utils).
  leaseChargesVat?: boolean;
}

export type SettlementStatus = "draft" | "confirmed";
export type SettlementScope = "group" | "unit";
export type SettlementActionType = "invite" | "deduct" | "refund" | "balance";

export interface SettlementAction {
  contractId: string;
  type: SettlementActionType;
  amount: number;
  // Set once the betaaluitnodiging is sent from the confirmed view — links the
  // action to the created payment order so the payment status can be tracked.
  paymentOrderId?: number;
  // Bulk creation puts every tenant's request under ONE payment order, so the
  // order id alone can't tell them apart. When known, this pins the tenant's
  // own payment request for an exact per-tenant status.
  paymentRequestId?: number;
  // Manual "Zet betaalstatus op betaald" for THIS tenant (from the confirmed
  // view) — overrides the derived status until toggled off again.
  manualPaidAt?: string;
  // "Mark as refunded" for THIS tenant's refund (a "refund" action):
  // the day the office paid it back outside Rentio, which never pays a tenant
  // back itself. A marked refund counts as done in the lists and blocks
  // deleting the settlement (decision D6 of 2026-10-15).
  refundedAt?: string;
  // A settlement confirmed before 2026-10-20 may still hold the document its
  // statement went out as (statementDocumentId, statementSharedAt on an
  // action, ownerStatementDocuments on the snapshot). Rentio no longer sends
  // statements, nothing reads them, and the API keeps them as stored.
}

// A provision the broker added by hand in the Provisies step, with a title of
// their own: an advance the tenant paid that the lease does not show (e.g. a
// separate heating advance). It comes off the tenant's balance like the
// lease's provisions (Lewis, 2026-10-08).
export interface IExtraProvision {
  id: string;
  title: string;
  amount: number;
  // The kind it counts in, as the broker chose it (decision 20, 2026-10-20):
  // "common" or "private". Absent on one added before that choice existed,
  // which counts in the private kind (see addedProvisionKindOf).
  kind?: "common" | "private";
}

// The title a provision added by hand goes by: the broker's own, or a
// generic one when they left it empty.
export const extraProvisionTitle = (extra: IExtraProvision): string =>
  extra.title.trim() ||
  getLocalizedText("settlement.provisions.extra_fallback_title");

// The part of a tenant's provision that the lease gave, without the ones
// the broker added by hand.
export const leaseProvisionOf = (
  result: Pick<PerContractResult, "provision" | "extraProvisions">,
): number =>
  roundCents(
    result.provision -
      (result.extraProvisions ?? []).reduce(
        (sum, extra) => sum + extra.amount,
        0,
      ),
  );

// A lease settled on its own when its tenant left (a departure settlement),
// as a later settlement of the same costs reads it: the costs it settled for
// that lease, frozen per cost (Lewis, 2026-10-08/09). The later settlement
// does not charge the lease those costs again up to `until`; any difference
// to what it computes for the lease up to then falls on the unit's owner. A
// lease that runs longer now pays its days after `until` there. Costs the
// departure did not hold are charged to the lease as usual.
export interface ISettledDeparture {
  settlementId: string;
  // The departure settlement as the lists name it (settlementDisplayName).
  name?: string;
  contractId: string;
  unitId: string;
  tenantName: string;
  // The last day the departure settled (the lease's end).
  until: string;
  // The lease's share per cost, excl. VAT, and its VAT.
  costByInvoice: Record<string, number>;
  vatByInvoice?: Record<string, number>;
  // The key the departure split each category by, when it split it by a
  // key: a later settlement with another key says what that moves to the
  // owner (decision 2). Absent on older snapshots.
  keyIdByCategory?: Record<string, string>;
  // The lease's provision days per slice the departure counted, and its
  // provision per slice: what a correction for a lease end that moved
  // earlier pays back (decision 3). Absent on older snapshots.
  provisionIntervalsBySlice?: Partial<
    Record<ProvisionSlice, { from: string; to: string }[]>
  >;
  provisionBySlice?: Partial<Record<ProvisionSlice, number>>;
}

// A cost share a departure settlement already settled, as a later
// settlement shows it ("Al afgerekend bij vertrek").
export interface IAlreadySettledLine {
  invoiceId: string;
  // The breakdown key of the cost, e.g. "Elevator" or "Water (privé)".
  label: string;
  settlementId: string;
  name?: string;
  contractId: string;
  unitId: string;
  tenantName: string;
  until: string;
  amount: number;
  vat: number;
  // What this settlement computes for the lease up to `until` minus
  // `amount`, excl. VAT: the owner of the unit carries it (see
  // OwnerDepartureDiff in engine.ts). Absent on older snapshots.
  difference?: number;
  // The lease end this settlement charged the lease up to (its days after
  // `until`), frozen: a lease end that moves later is compared with it, not
  // with `until` (M01). Absent on older snapshots.
  leaseEnd?: string;
}

export interface PerContractResult {
  contractId: string;
  // Real property id of the unit this contract belongs to — lets the UI/PDF
  // resolve the unit's full address (incl. bus number) for group settlements.
  unitId: string;
  unitCode: string;
  tenantName: string;
  cost: number; // gross allocated cost share (before provisions)
  // VAT over exactly that share, summed per cost at its OWN rate. Optional:
  // snapshots frozen before this field existed fall back to a category average.
  costVat?: number;
  provision: number; // EXPECTED provision (monthly × months) — the settlement
  // reconciles the actual cost against what the tenant was expected to pay,
  // never against what was actually collected in Rentio (team decision).
  // Includes the extra provisions below.
  // The provisions the broker added by hand; absent when there are none.
  extraProvisions?: IExtraProvision[];
  // Per breakdown key, the part that is this tenant's share of a
  // consumption excess split by the key (Y14): its reading shows as
  // "30 m³ + aandeel overschot € 75,34". Absent when there is none.
  excessByCat?: Record<string, number>;
  // The same per cost (keyed by cost id).
  excessByInvoice?: Record<string, number>;
  // Per correction of a lease end that moved (decision 3; its breakdown
  // key), the part of its amount that is provision paid back (+) or given
  // back (-): no VAT, and the statement prints it as a provision. Absent
  // when there is none.
  correctionProvisionByKey?: Record<string, number>;
  // Per slice, the days of the period its kind was a forfait while its
  // other days are settled (the lease switched inside the period, N71):
  // those days carry no cost and no provision. Absent when there are none.
  forfaitIntervalsBySlice?: Partial<
    Record<"common" | "private", { from: string; to: string }[]>
  >;
  balance: number; // net = cost - provision (expected)
  // VAT per breakdown key, per cost at its own rate and rounded per cost —
  // the same figures the statement PDF prints. Absent on older snapshots.
  breakdownVat?: Record<string, number>;
  // VAT per cost (keyed by cost id): this tenant's cent-exact part of that
  // invoice's VAT, which the statement PDF prints per line. Absent on older
  // snapshots.
  vatByInvoice?: Record<string, number>;
  // The lease end this settlement settled the lease up to (frozen on
  // confirm; absent on a departure settlement, whose `departure.until` it
  // is, and on older snapshots). A later settlement corrects the days
  // between it and the lease's end then (decision 3; see
  // departureCorrection.utils).
  leaseEnd?: string;
  // This tenant's share per cost (keyed by cost id), excl. VAT. A departure
  // settlement freezes it: a later settlement charges the departed tenant
  // none of those costs again. Absent on older snapshots.
  costByInvoice?: Record<string, number>;
  // The provision days this settlement counted for the contract (frozen on
  // confirm), of any slice: the union of provisionIntervalsBySlice. Absent
  // on older snapshots.
  provisionIntervals?: { from: string; to: string }[];
  // The ledger record: per slice of the provision, the days this settlement
  // counted. A later settlement anywhere in the property tree skips them for
  // that slice, so a month of provision is settled once per slice and per
  // lease. Absent on older snapshots: their provisionIntervals then hold for
  // the slices legacyProvisionSlices infers (settledProvisions.utils).
  provisionIntervalsBySlice?: Partial<
    Record<ProvisionSlice, { from: string; to: string }[]>
  >;
  // The lease's own provision per slice, without the extra provisions;
  // absent on older snapshots and when the broker typed in one amount for
  // the whole lease.
  provisionBySlice?: Partial<Record<ProvisionSlice, number>>;
  // The months each slice counted.
  provisionMonthsBySlice?: Partial<Record<ProvisionSlice, number>>;
  // Per slice it counted, the provision lines the broker left out (decision
  // 14, 2026-10-20): they stay open per line for those days, and a later
  // settlement counts them. Absent when there are none.
  provisionLinesLeftOut?: Partial<Record<ProvisionSlice, string[]>>;
  // Per provision line, the days this settlement counted for that line
  // alone: a line an earlier settlement left out. Absent when there are
  // none.
  provisionLineIntervals?: Record<string, { from: string; to: string }[]>;
  // The tenant's balance per kind of cost (Lewis, 2026-10-16; see
  // settlementKinds.utils): the costs, provisions and balance of each kind,
  // adding up to the cent to cost, costVat and provision. Absent on older
  // snapshots (one balance), and when the kinds cannot be told apart (one
  // amount typed in for the whole lease of a lease that weighs two slices).
  kinds?: ISettlementKind[];
  // The kinds this settlement did not settle with the tenant because the
  // lease prices them as a forfait (Lewis, 2026-10-16): the tenant's share of
  // their costs went to the owner, and no provision of theirs counted. Frozen
  // so the confirmed view and the statement never recompute it. Absent when
  // there are none, and on older snapshots.
  fixedFeeKinds?: TFixedFeeKindKey[];
  // Provision Rentio billed for days outside the lease, e.g. a month billed
  // in full before the move-out was registered. For information only: never
  // part of the balance, the broker returns it outside Rentio (C.12). Absent
  // when nothing was billed outside the lease.
  provisionBilledOutsideContract?: number;
  breakdown: Record<string, number>;
  // Frozen on confirm (not set by the engine): the payer the settlement was
  // addressed to, and everyone on the lease, so editing or deleting the
  // contract later changes neither. Absent on older snapshots.
  payerAccountId?: number;
  tenantNames?: string[];
  parentNames?: string[];
  // The accounts of the lease's tenants and parents (see
  // settledProvisions.utils): a later lease of one of them in the same unit
  // does not count these provision days again.
  leaseAccountIds?: number[];
}

// A cost as a settlement settled it: the figures the API compares with the
// cost when it confirms (a cost changed in another tab meanwhile refuses the
// confirm), and that a confirmed settlement shows instead of the live cost.
export interface IInvoiceFigures {
  // The property the cost is booked on.
  propertyId: number;
  amountExclVat: number;
  vat: VatValue;
  periodFrom: string;
  periodTo: string;
  target: InvoiceTarget;
  tenantSharePct?: number;
  category: Category;
}

export interface SettlementSnapshot {
  // The settlement's name, as the broker gave it in the period step ("Naam",
  // prefilled with getSettlementTitle; Lewis, 2026-10-16), frozen on
  // confirm. The lists, the detail, the statement and the payment use it;
  // a settlement without one is named by getSettlementTitle
  // (settlementName.utils).
  name?: string;
  // The owner part per owner (see engine OwnerResult) and their names, frozen
  // on confirm. Absent on older snapshots: one owner card from the totals.
  owners?: OwnerResult[];
  // Set by the API when the viewer only sees their own part of a group
  // settlement (an owner of some of its units): other tenants and owners
  // were left out of this snapshot.
  viewerPart?: boolean;
  // Per building cost split by readings or a metering company, each unit's
  // part excl. VAT as the settlement split it (Y16; see the engine's
  // ComputeResult.unitSharesByInvoice). The API trims it to the viewer's own
  // units for an owner. Absent on older snapshots.
  unitSharesByInvoice?: Record<string, Record<string, number>>;
  // The corrections of departures whose lease end moved, this settlement
  // made (decision 3; see departureCorrection.utils). Absent when none.
  departureCorrections?: IDepartureCorrection[];
  // With viewerPart: how many units the group has (the viewer only sees
  // some of them), for the equal key's "1 / n".
  groupUnitCount?: number;
  // Group settlements: every unit the settlement counted (vacant ones
  // included) with its label on confirm. Its length is the equal key's
  // denominator; an archived or renamed unit keeps its label. The API trims
  // it to the viewer's own units for an owner (groupUnitCount keeps the
  // count). Absent on older snapshots, which read the live property list.
  // `archivedOn`: the day a unit counted here had been archived, so a cost
  // that began after it keeps showing the split without that unit.
  groupUnits?: { id: string; label: string; archivedOn?: string }[];
  // With viewerPart: every unit of groupUnits before the API trimmed it,
  // without its label, so a key's "Verdeling" divides by the units of each
  // cost's period as the engine did (a unit archived before the cost began
  // takes no part in it).
  allGroupUnits?: { id: string; archivedOn?: string }[];
  // The settled property's label (the unit, or the group) on confirm.
  propertyLabel?: string;
  // Apportionment key names on confirm, by key id.
  keyNames?: Record<string, string>;
  ownerNames?: Record<string, string>;
  // Every statement document as it was on confirm, keyed "tenant:<contractId>"
  // or "owner:<ownerKey>". A later download prints exactly these, so editing a
  // cost, key or contract afterwards cannot change what was sent. Absent on
  // older snapshots, which rebuild from live data.
  statements?: Record<string, StatementSnapshot>;
  invoiceIds: string[];
  // Each settled cost as the wizard read it (see IInvoiceFigures). The API
  // refuses a confirm when a cost no longer matches them, and a confirmed
  // settlement shows its costs with them. Absent on older snapshots; the API
  // leaves an owner only those of the group and of their own units.
  invoiceFigures?: Record<string, IInvoiceFigures>;
  keySnapshots: Record<string, Record<string, number>>;
  provisionExpectedOverride?: Record<string, number>;
  provisionExpectedOverrideBySlice?: Record<
    string,
    Partial<Record<ProvisionSlice, number>>
  >;
  // The slices of the provision this settlement weighed. Absent on older
  // snapshots, which legacyProvisionSlices infers (settledProvisions.utils).
  provisionSlices?: ProvisionSlice[];
  perContract: PerContractResult[];
  totCost: number;
  // Total of ALL settled invoices (tenants + owner share), excl. btw and the
  // btw on top — frozen so the settlements list can show the settlement's full
  // amount without refetching the costs.
  costTotalExcl?: number;
  costTotalBtw?: number;
  net: number;
  landlordTotal: number;
  actions: SettlementAction[];
  // Selected apportionment key per category at confirm time — needed to
  // regenerate the statement PDF from the frozen snapshot (the wizard draft
  // that held this choice is cleared on confirm).
  selectedKeyId?: Partial<Record<Category, string>>;
  // The rest of the distribution choice per category, frozen alongside the key:
  // without these the statement PDF had to assume "key" and produced different
  // amounts than the Samenvatting for verbruik-/bedrag-based categories.
  distributionMethod?: Record<string, DistributionMethod>;
  consumption?: Record<string, Record<string, number>>;
  buildingTotal?: Record<string, number>;
  excessMode?: Record<string, "key" | "landlord">;
  companyAmounts?: Record<string, Record<string, number>>;
  // Owner-share detail per category (structural share, vacancy fallback and
  // key percentage) — lets the ConfirmedView render the exact same owner card
  // as the wizard's Samenvatting from the frozen snapshot.
  landlordBaseByCat?: Record<string, number>;
  landlordVacancyByCat?: Record<string, number>;
  landlordPctByCat?: Record<string, number>;
  // VAT the owner carries per category, summed at each cost's own rate.
  landlordVatByCat?: Record<string, number>;
  // A departure settlement (see ISettledDeparture): the one lease it settled,
  // on its unit, up to its last day. It leaves every cost open for the rest
  // of the unit and the group.
  departure?: {
    contractId: string;
    unitId: string;
    until: string;
    tenantName: string;
  };
  // The cost shares earlier departure settlements already settled, as this
  // settlement showed them ("Al afgerekend bij vertrek").
  alreadySettled?: IAlreadySettledLine[];
}

// A departure settlement settles one lease and leaves the rest of every cost
// open: it never makes a cost "settled" for the unit or the group.
export const isDepartureSettlement = (
  settlement: Pick<Settlement, "snapshot">,
): boolean => !!settlement.snapshot?.departure;

// What a departure settlement settled, as a later settlement of the same
// costs reads it (see ISettledDeparture). Undefined for any other settlement.
export const settledDepartureOf = (
  settlement: Settlement,
): ISettledDeparture | undefined => {
  const departure = settlement.snapshot?.departure;
  if (settlement.status !== "confirmed" || !departure) return undefined;
  const row = settlement.snapshot?.perContract.find(
    result => result.contractId === departure.contractId,
  );
  return {
    settlementId: settlement.id,
    name: settlementDisplayName(settlement),
    contractId: departure.contractId,
    unitId: departure.unitId,
    tenantName: departure.tenantName,
    until: departure.until,
    costByInvoice: row?.costByInvoice ?? {},
    vatByInvoice: row?.vatByInvoice,
    ...(row?.provisionIntervalsBySlice
      ? { provisionIntervalsBySlice: row.provisionIntervalsBySlice }
      : {}),
    ...(row?.provisionBySlice
      ? { provisionBySlice: row.provisionBySlice }
      : {}),
    ...(settlement.snapshot?.selectedKeyId
      ? {
          keyIdByCategory: Object.fromEntries(
            Object.entries(settlement.snapshot.selectedKeyId).filter(
              ([category, keyId]) =>
                !!keyId &&
                resolveDistributionMethod(
                  category as Category,
                  settlement.snapshot?.distributionMethod,
                  { isDeparture: true },
                ) === "key",
            ) as Array<[string, string]>,
          ),
        }
      : {}),
  };
};

export interface SettlementDraftState {
  stepIndex: number;
  // The name the broker typed in "Naam". Undefined until they edit the
  // field: the wizard then suggests one that follows the period
  // (getDraftSettlementName).
  name?: string;
  // The period overlaps a confirmed settlement of the property tree (M22):
  // the suggested name is "Aanvulling <periode>" instead of "Afrekening …".
  // Kept up to date with the period by the period step.
  isSupplementary?: boolean;
  period: { from: string; to: string };
  selectedInvoiceIds: Record<string, boolean>;

  distributionMethod: Record<string, DistributionMethod>;
  selectedKeyId: Record<string, string>;
  consumption: Record<string, Record<string, number>>;
  buildingTotal: Record<string, number>;
  excessMode: Record<string, "key" | "landlord">;
  companyAmounts: Record<string, Record<string, number>>;

  // Expected provision (total over the settled period) the broker typed in
  // for a contract, replacing the one computed from the provision history.
  // Keyed by contract id; absent = computed. The one amount for the whole
  // lease of drafts made before the slices; a slice's own amount
  // (provisionExpectedOverrideBySlice) comes first.
  provisionExpectedOverride?: Record<string, number>;
  // Per contract id and slice: the expected provision of that slice the
  // broker typed in.
  provisionExpectedOverrideBySlice?: Record<
    string,
    Partial<Record<ProvisionSlice, number>>
  >;
  // Per contract id and provision line (IProvisionLine.key): the amount the
  // broker typed in for that line in the provisions step. Display only and
  // draft only: each typed line is already summed into its slice's amount in
  // provisionExpectedOverrideBySlice, which is what the engine bills and the
  // snapshot keeps (see provisionLineRows.utils).
  provisionExpectedOverrideByLine?: Record<string, Record<string, number>>;

  // Whether a contract's provisions are included in the settlement balance.
  // Explicitly `false` opts out the whole lease — that contract's provisie
  // regel is shown but its provision does NOT reduce its balance (drafts
  // made before the lines; see provisionLineInclusion.utils).
  provisionsIncluded?: Record<string, boolean>;
  // Per contract id and provision line: `true` or `false` for a line the
  // broker ticked or unticked; absent = ticked (see
  // provisionLineInclusion.utils for the keys).
  provisionLinesIncluded?: Record<string, Record<string, boolean>>;
  // The broker confirmed in "Keuze van kosten" that the period had no costs
  // (Lewis, 2026-10-17): with no cost chosen the settlement may only go on
  // once it is ticked, as the tenants then get their whole provision back.
  // Choosing a cost clears it. Draft only: a confirmed settlement without
  // costs says so from its empty cost list (isWithoutCosts).
  noCostsConfirmed?: boolean;

  // Provisions the broker added by hand, per contract id (see
  // IExtraProvision). An opted-out contract counts none of them either.
  extraProvisions?: Record<string, IExtraProvision[]>;

  // A departure settlement: the lease whose tenant leaves, and its last day
  // when the draft was made. Confirming freezes the lease's end as it is
  // then (snapshot.departure.until), the day the engine settled up to.
  departure?: { contractId: string; until: string };

  actions: Record<string, SettlementActionType>;
}

export interface Settlement {
  id: string;
  groupId?: string;
  unitId?: string;
  scope: SettlementScope;
  status: SettlementStatus;
  periodFrom: string;
  periodTo: string;
  createdAt: string;
  confirmedAt?: string;
  // Persisted by the API but unused by the frontend (no UI ever set it).
  delayDays?: number;
  draftState?: SettlementDraftState;
  snapshot?: SettlementSnapshot;
}

// Unit shown for "actual consumption" per category.
export const CONSUMPTION_UNIT: Partial<Record<Category, string>> = {
  Water: "m³",
  Electricity: "kWh",
  Gas: "m³",
  Heating: "kWh",
  // A word, unlike the symbols above, so it follows the user's language; read
  // at access time because the language is not known at module load.
  get HeatingOil() {
    return getLocalizedText("costs.unit.liter");
  },
};

const DISTRIBUTION_METHOD_KEYS: Record<DistributionMethod, string> = {
  key: "costs.distribution_method.key",
  consumption: "costs.distribution_method.consumption",
  company: "costs.distribution_method.company",
};

// Allowed distribution methods for a category (logic per Rentio standard).
export function methodsForCategory(category: Category): DistributionMethod[] {
  return CATEGORY_META[category]?.methods ?? ["key"];
}

// A departure settlement settles one tenant mid-year, while readings exist
// only for the whole year (Lewis, 2026-10-09). Instead of the consumption it
// offers the metering company's amount for that tenant ("Bedrag voor deze
// huurder", e.g. from an interim statement), next to the key.
export function departureMethodsForCategory(
  category: Category,
): DistributionMethod[] {
  return [
    ...new Set(
      methodsForCategory(category).map(method =>
        method === "consumption" ? "company" : method,
      ),
    ),
  ];
}

// The distribution method in effect for a category: the explicit choice when the
// user made one, otherwise the category's own default. The engine, the wizard and
// the statement PDF all resolve it here, so a method shown in the UI can never
// disagree with the one the calculation used. A departure settlement takes the
// tenant's amount where the consumption would need readings of the year.
export function resolveDistributionMethod(
  category: Category,
  methodByCategory?: Record<string, DistributionMethod>,
  options?: { isDeparture?: boolean },
): DistributionMethod {
  const chosen = methodByCategory?.[category];
  if (options?.isDeparture) {
    if (chosen === "consumption") return "company";
    return chosen ?? departureMethodsForCategory(category)[0] ?? "key";
  }
  return chosen ?? methodsForCategory(category)[0] ?? "key";
}

export function methodLabel(
  method: DistributionMethod,
  category: Category,
  options?: { isDeparture?: boolean },
): string {
  // A departure settlement asks the amount for its one tenant.
  if (method === "company" && options?.isDeparture) {
    return getLocalizedText("settlement.departure.method_tenant_amount");
  }
  if (method === "consumption") {
    const unit = CONSUMPTION_UNIT[category];
    return unit
      ? getLocalizedText("costs.distribution_method.consumption_with_unit", {
          unit,
        })
      : getLocalizedText("costs.distribution_method.consumption");
  }
  return getLocalizedText(DISTRIBUTION_METHOD_KEYS[method]);
}

// A payment was asked for a cost from before payment splits: only the
// cost-level date says so. With splits, their links say whether a payment is
// pending; the date can outlive a payment that was withdrawn since.
const isPaymentRequestedWithoutSplits = (invoice: Invoice): boolean =>
  !!invoice.paymentRequestedAt && (invoice.paymentSplits ?? []).length === 0;

// A cost the API refuses today, but an older record can still hold: no
// positive amount (a credit note is entered as a corrected cost instead), a
// period that ends before it starts, or one longer than the cost popup allows
// (a typing error like 2000 to 2050). Spread over its period it would lose
// money or land on decades of tenants, so it stays out of a settlement until
// it is corrected.
const isInvalidCost = (invoice: Invoice): boolean =>
  !(invoice.amountExclVat > 0) ||
  invoice.periodTo < invoice.periodFrom ||
  (!!invoice.periodFrom &&
    !!invoice.periodTo &&
    isCostPeriodTooLong({ from: invoice.periodFrom, to: invoice.periodTo }));

export function settlementEligible(invoice: Invoice): boolean {
  // Costs that show up on an afrekening — either payable by a tenant (target
  // "settlement"), split between tenant and owner ("shared"), or fully owner
  // ("owner", surfaces on the eigenaar-card). Direct-to-tenant costs bypass
  // the settlement flow.
  if (
    invoice.target !== "settlement" &&
    invoice.target !== "shared" &&
    invoice.target !== "owner"
  ) {
    return false;
  }
  if (isInvalidCost(invoice)) return false;
  // Fully paid: manual paidAt or all splits paid.
  if (invoice.paidAt) return false;
  if (isPaymentRequestedWithoutSplits(invoice)) return false;
  // Multi-payer flow: any per-payer split that has payment activity
  // (invitation sent, payment order linked, request created or paid)
  // means the cost is no longer up for settlement.
  const splits = invoice.paymentSplits ?? [];
  if (
    splits.some(
      s =>
        !!s.requestedAt ||
        !!s.paidAt ||
        !!s.paymentOrderId ||
        s.paymentRequestId != null,
    )
  ) {
    return false;
  }
  return true;
}

// Why a cost cannot go into a settlement, in the broker's words. Returns
// undefined when the cost IS eligible. The wizard shows the ineligible costs
// in their own card with this reason, so a cost the broker just saw in the
// cost list is never silently absent from the settlement.
export function settlementIneligibleReason(
  invoice: Invoice,
): string | undefined {
  if (settlementEligible(invoice)) return undefined;
  if (invoice.target === "direct" && !invoice.paidAt) {
    return getLocalizedText("settlement.ineligible.direct_request");
  }
  if (isInvalidCost(invoice)) {
    return getLocalizedText("settlement.ineligible.invalid_cost");
  }
  if (invoice.paidAt) return getLocalizedText("settlement.ineligible.paid");
  if (isPaymentRequestedWithoutSplits(invoice)) {
    return getLocalizedText("settlement.ineligible.request_pending");
  }
  const splits = invoice.paymentSplits ?? [];
  if (splits.some(s => !!s.paidAt))
    return getLocalizedText("settlement.ineligible.partly_paid");
  if (
    splits.some(
      s => !!s.requestedAt || !!s.paymentOrderId || s.paymentRequestId != null,
    )
  ) {
    return getLocalizedText("settlement.ineligible.request_pending");
  }
  return getLocalizedText("settlement.ineligible.generic");
}

// Per-payer derived helpers — drive the kostenlijst-badge + view-modal section.
export const paidCount = (invoice: Invoice): number =>
  invoice.paymentSplits?.filter(s => !!s.paidAt).length ?? 0;

export const requestedCount = (invoice: Invoice): number =>
  invoice.paymentSplits?.filter(s => !!s.requestedAt).length ?? 0;

export const allSplitsPaid = (invoice: Invoice): boolean =>
  !!invoice.paymentSplits?.length &&
  invoice.paymentSplits.every(s => !!s.paidAt);

// Overall "betaald op …" date for the cost. Legacy single-field paidAt wins
// when present; otherwise we use the latest split.paidAt iff all are paid.
export const effectivePaidAt = (invoice: Invoice): string | undefined => {
  if (invoice.paidAt) return invoice.paidAt;
  if (!allSplitsPaid(invoice)) return undefined;
  return invoice
    .paymentSplits!.map(s => s.paidAt!)
    .sort()
    .at(-1);
};

// Amount that actually flows to the settlement (after shared-split). The
// tenant slice of a shared cost is cent-exact, the same split as the cost
// popup and a unit's own costs, so the owner keeps exactly the remainder.
export function settlementAmount(invoice: Invoice): number {
  // An ownership cost never reaches the tenant, whatever an older record says.
  if (isOwnerOnlyCategory(invoice.category)) return 0;
  if (invoice.target === "shared") {
    return fromCents(
      tenantShareCents({
        totalCents: toCents(invoice.amountExclVat),
        tenantSharePct: invoice.tenantSharePct ?? 0,
      }),
    );
  }
  if (invoice.target === "settlement") return invoice.amountExclVat;
  return 0;
}

// The tenant has paid once the money reached Rentio — from paid_in onward,
// including the transfer to the owner. A payout to the owner that fails after
// that (KYC, transfer, pay-out) leaves the tenant's payment paid. Must match
// the server's rule in PaymentRequestService.hasOpenRequests
// (rentio-api-next), or the dashboard and the API disagree on whether a
// payment is still open.
export const TENANT_PAID_STATUSES: readonly string[] = [
  EPaymentRequestStatus.PaidIn,
  EPaymentRequestStatus.Transferring,
  EPaymentRequestStatus.Transferred,
  EPaymentRequestStatus.PayingOut,
  EPaymentRequestStatus.Paid,
  EPaymentRequestStatus.FailedKyc,
  EPaymentRequestStatus.FailedPayout,
  EPaymentRequestStatus.FailedTransfer,
];
export const isTenantPaidStatus = (status?: string | null): boolean =>
  status != null && TENANT_PAID_STATUSES.includes(status);
